import type { TomSemantico } from '../componentes/ui';
import { requisicaoAutenticada } from './http';

/**
 * CRM — o pipeline de acompanhamento das empresas (`/api/crm`).
 *
 * Espelha `apps/api/src/modules/crm/crm.service.ts`. Da operadora, só admin;
 * em quadros ("Comercial", "Operacional"…), cada um com as suas etapas.
 */

export interface TagDoCrm {
  readonly id: string;
  readonly nome: string;
  readonly cor: TomSemantico;
  /** Nascida do catálogo de serviços: não se exclui por aqui. */
  readonly deServico: boolean;
}

export interface PessoaDoCrm {
  readonly accountId: string;
  readonly nome: string;
}

export interface CardResumo {
  readonly id: string;
  readonly quadroId: string;
  readonly colunaId: string;
  readonly ordem: number;
  readonly businessId: string;
  readonly razaoSocial: string;
  readonly nomeFantasia: string | null;
  readonly cnpj: string;
  /** `AAAA-MM-DD`, ou `null`. */
  readonly dataExecucao: string | null;
  readonly tagIds: readonly string[];
  readonly comentarios: number;
  readonly participantes: readonly PessoaDoCrm[];
  /** Atividades em aberto no card. */
  readonly atividadesPendentes: number;
  /** O prazo mais próximo entre elas, `AAAA-MM-DD`. */
  readonly proximoPrazo: string | null;
}

export interface ColunaDoQuadro {
  readonly id: string;
  readonly nome: string;
  readonly ordem: number;
  readonly cards: readonly CardResumo[];
}

/** Um quadro no seletor do Pipeline. */
export interface QuadroResumo {
  readonly id: string;
  readonly nome: string;
  readonly ordem: number;
  readonly cards: number;
}

export interface Quadro {
  /** O quadro aberto. */
  readonly id: string;
  readonly nome: string;
  readonly colunas: readonly ColunaDoQuadro[];
  readonly tags: readonly TagDoCrm[];
}

export interface ColaboradorDaEmpresa {
  readonly nome: string;
  readonly emailAddress: string;
  readonly telefone: string | null;
  readonly cargo: string | null;
  readonly papel: 'Gestor' | 'Colaborador';
  readonly assinaPelaEmpresa: boolean;
}

export interface ComentarioDoCard {
  readonly id: string;
  readonly texto: string;
  readonly autor: PessoaDoCrm;
  readonly criadoEm: string;
  readonly editado: boolean;
}

export interface CardDetalhado extends CardResumo {
  readonly colunaNome: string;
  readonly criadoEm: string;
  readonly empresa: {
    readonly emailAddress: string;
    readonly telefone: string | null;
    readonly regimeTributario: string | null;
    readonly naturezaJuridica: string | null;
    readonly cnae: string | null;
    readonly cadIcms: string | null;
    readonly credencialSiscred: string | null;
    readonly endereco: string | null;
    readonly perfis: {
      readonly cliente: boolean;
      readonly assessor: boolean;
      readonly fornecedor: boolean;
    };
    readonly cadastradaEm: string;
  };
  readonly colaboradores: readonly ColaboradorDaEmpresa[];
  /** Contatos da empresa sem acesso ao Deca — anotados no card ou vindos da Prospecção. */
  readonly contatos: readonly ContatoDoCrm[];
  readonly comentariosDoCard: readonly ComentarioDoCard[];
  /** As tags do card com captadores ativados. */
  readonly tagsComCaptadores: readonly string[];
}

/** Os tons que uma tag pode ter, com o nome que a tela mostra. */
export const CORES_DE_TAG: readonly { readonly tom: TomSemantico; readonly rotulo: string }[] = [
  { tom: 'informativo', rotulo: 'Azul' },
  { tom: 'progresso', rotulo: 'Roxo' },
  { tom: 'positivo', rotulo: 'Verde' },
  { tom: 'atencao', rotulo: 'Laranja' },
  { tom: 'critico', rotulo: 'Vermelho' },
  { tom: 'neutro', rotulo: 'Cinza' },
];

export const CHAVE_DOS_QUADROS = ['crm', 'quadros'] as const;

/** O último quadro aberto neste navegador — Pipeline e Relatórios abrem por ele. */
const CHAVE_DO_ULTIMO_QUADRO = 'deca.crm.quadro';

export function lerUltimoQuadro(): string | null {
  try {
    return window.localStorage.getItem(CHAVE_DO_ULTIMO_QUADRO);
  } catch {
    return null;
  }
}

export function gravarUltimoQuadro(id: string): void {
  try {
    window.localStorage.setItem(CHAVE_DO_ULTIMO_QUADRO, id);
  } catch {
    // Sem armazenamento, abre no primeiro quadro — nada quebra.
  }
}

/** A chave do quadro aberto; `null` = o padrão, enquanto a lista de quadros não chega. */
export const chaveDoQuadro = (quadroId: string | null) => ['crm', 'quadro', quadroId] as const;

export const carregarQuadros = (): Promise<QuadroResumo[]> =>
  requisicaoAutenticada<QuadroResumo[]>('/crm/quadros');

export const carregarQuadro = (quadroId: string | null): Promise<Quadro> =>
  requisicaoAutenticada<Quadro>(
    quadroId === null ? '/crm/quadro' : `/crm/quadro?quadroId=${encodeURIComponent(quadroId)}`,
  );

export const criarQuadro = (dados: {
  readonly nome: string;
  readonly copiarEtapasDe?: string;
}): Promise<QuadroResumo> =>
  requisicaoAutenticada<QuadroResumo>('/crm/quadros', { metodo: 'POST', corpo: dados });

export const renomearQuadro = (id: string, nome: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/quadros/${id}`, { metodo: 'PATCH', corpo: { nome } });

export const excluirQuadro = (id: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/quadros/${id}`, { metodo: 'DELETE' });

export const carregarCard = (id: string): Promise<CardDetalhado> =>
  requisicaoAutenticada<CardDetalhado>(`/crm/cards/${id}`);

export const criarColuna = (quadroId: string, nome: string): Promise<void> =>
  requisicaoAutenticada<void>('/crm/colunas', { metodo: 'POST', corpo: { quadroId, nome } });

export const editarColuna = (
  id: string,
  dados: { readonly nome?: string; readonly ordem?: number },
): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/colunas/${id}`, { metodo: 'PATCH', corpo: dados });

export const excluirColuna = (id: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/colunas/${id}`, { metodo: 'DELETE' });

/** A coluna decide o quadro; sem coluna nem quadro, entra no quadro padrão. */
export const criarCard = (
  businessId: string,
  destino: { readonly quadroId?: string; readonly colunaId?: string } = {},
): Promise<CardResumo> =>
  requisicaoAutenticada<CardResumo>('/crm/cards', {
    metodo: 'POST',
    corpo: {
      businessId,
      ...(destino.quadroId === undefined ? {} : { quadroId: destino.quadroId }),
      ...(destino.colunaId === undefined ? {} : { colunaId: destino.colunaId }),
    },
  });

/** A mesma empresa em outro quadro, na primeira etapa, com as tags e quem acompanha. */
export const copiarCard = (id: string, quadroId: string): Promise<CardResumo> =>
  requisicaoAutenticada<CardResumo>(`/crm/cards/${id}/copia`, {
    metodo: 'POST',
    corpo: { quadroId },
  });

export const definirData = (id: string, dataExecucao: string | null): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/cards/${id}`, { metodo: 'PATCH', corpo: { dataExecucao } });

/** Onde a automação do CRM pôs um card fora do quadro aberto (D-011). */
export interface AutomacaoAplicada {
  readonly cardId: string;
  readonly quadroId: string;
  readonly quadroNome: string;
  readonly colunaNome: string;
}

/**
 * Move o card — e diz o que a automação fez com isso: entrar em Comercial ›
 * Contrato assinado leva uma cópia a Operacional › Em trâmite.
 */
export const moverCard = (
  id: string,
  colunaId: string,
  indice: number,
): Promise<{ readonly automacao: AutomacaoAplicada | null }> =>
  requisicaoAutenticada<{ readonly automacao: AutomacaoAplicada | null }>(
    `/crm/cards/${id}/posicao`,
    { metodo: 'PUT', corpo: { colunaId, indice } },
  );

export const excluirCard = (id: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/cards/${id}`, { metodo: 'DELETE' });

export const definirTags = (id: string, tagIds: readonly string[]): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/cards/${id}/tags`, { metodo: 'PUT', corpo: { tagIds } });

export const definirParticipantes = (id: string, accountIds: readonly string[]): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/cards/${id}/participantes`, {
    metodo: 'PUT',
    corpo: { accountIds },
  });

export const comentar = (id: string, texto: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/cards/${id}/comentarios`, { metodo: 'POST', corpo: { texto } });

export const editarComentario = (id: string, texto: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/comentarios/${id}`, { metodo: 'PATCH', corpo: { texto } });

export const excluirComentario = (id: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/comentarios/${id}`, { metodo: 'DELETE' });

export const criarTag = (nome: string, cor: TomSemantico): Promise<TagDoCrm> =>
  requisicaoAutenticada<TagDoCrm>('/crm/tags', { metodo: 'POST', corpo: { nome, cor } });

export const excluirTag = (id: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/tags/${id}`, { metodo: 'DELETE' });

export const carregarParticipantesPossiveis = (): Promise<PessoaDoCrm[]> =>
  requisicaoAutenticada<PessoaDoCrm[]>('/crm/participantes-possiveis');

// ─── Datas ──────────────────────────────────────────────────────────────────

/** `AAAA-MM-DD` de hoje, no fuso de quem usa — não em UTC. */
export function hojeIso(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

/** `2026-10-15` → `15/10/2026`, sem passar por `Date` (que puxaria o fuso). */
export function formatarDataIso(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** Vencida, hoje ou futura — a cor do prazo no card. */
export function situacaoDoPrazo(iso: string): 'vencida' | 'hoje' | 'futura' {
  const hoje = hojeIso();
  if (iso < hoje) return 'vencida';
  return iso === hoje ? 'hoje' : 'futura';
}

export interface CardDaEmpresa {
  /** Um por quadro em que a empresa está, na ordem dos quadros. Vazio: fora do CRM. */
  readonly cards: readonly {
    readonly id: string;
    readonly quadroId: string;
    readonly quadroNome: string;
    readonly colunaNome: string;
  }[];
}

export const carregarCardDaEmpresa = (businessId: string): Promise<CardDaEmpresa> =>
  requisicaoAutenticada<CardDaEmpresa>(`/crm/empresas/${businessId}/card`);

// ─── Atividades ─────────────────────────────────────────────────────────────

export type TipoDeAtividade = 'LIGACAO' | 'EMAIL' | 'REUNIAO' | 'DOCUMENTO' | 'TAREFA';

export const TIPOS_DE_ATIVIDADE: readonly {
  readonly tipo: TipoDeAtividade;
  readonly rotulo: string;
}[] = [
  { tipo: 'LIGACAO', rotulo: 'Ligação' },
  { tipo: 'EMAIL', rotulo: 'E-mail' },
  { tipo: 'REUNIAO', rotulo: 'Reunião' },
  { tipo: 'DOCUMENTO', rotulo: 'Documento' },
  { tipo: 'TAREFA', rotulo: 'Tarefa' },
];

export function rotuloDoTipo(tipo: string): string {
  return TIPOS_DE_ATIVIDADE.find((t) => t.tipo === tipo)?.rotulo ?? 'Tarefa';
}

export interface AtividadeDoCrm {
  readonly id: string;
  readonly titulo: string;
  readonly tipo: TipoDeAtividade;
  readonly descricao: string | null;
  /** `AAAA-MM-DD`. */
  readonly prazo: string;
  readonly hora: string | null;
  readonly responsavel: PessoaDoCrm;
  readonly concluidaEm: string | null;
  readonly card: {
    readonly id: string;
    readonly businessId: string;
    readonly razaoSocial: string;
    readonly nomeFantasia: string | null;
  };
}

export interface ListaDeAtividades {
  readonly itens: readonly AtividadeDoCrm[];
  readonly contagens: {
    readonly atrasadas: number;
    readonly hoje: number;
    readonly proximas: number;
  };
}

export interface DadosDeAtividade {
  readonly titulo: string;
  readonly tipo: TipoDeAtividade;
  readonly prazo: string;
  readonly hora: string | null;
  readonly responsavelId: string;
  readonly descricao: string;
}

export type VisaoDeAtividades = 'hoje' | 'proximas' | 'concluidas';

export const listarAtividades = (filtros: {
  readonly visao: VisaoDeAtividades;
  readonly responsavel: 'eu' | 'todos';
}): Promise<ListaDeAtividades> =>
  requisicaoAutenticada<ListaDeAtividades>(
    `/crm/atividades?${new URLSearchParams({ hoje: hojeIso(), ...filtros }).toString()}`,
  );

/**
 * As do período, pelo prazo — abertas e concluídas: o calendário pinta a
 * situação de cada uma. O período é o da grade (até 62 dias).
 */
export const listarAtividadesDoPeriodo = (filtros: {
  readonly de: string;
  readonly ate: string;
  readonly responsavel: 'eu' | 'todos';
}): Promise<ListaDeAtividades> =>
  requisicaoAutenticada<ListaDeAtividades>(
    `/crm/atividades?${new URLSearchParams({ hoje: hojeIso(), visao: 'calendario', ...filtros }).toString()}`,
  );

export const atividadesDoCard = (cardId: string): Promise<AtividadeDoCrm[]> =>
  requisicaoAutenticada<AtividadeDoCrm[]>(`/crm/cards/${cardId}/atividades`);

export const criarAtividade = (cardId: string, dados: DadosDeAtividade): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/cards/${cardId}/atividades`, { metodo: 'POST', corpo: dados });

export const editarAtividade = (
  id: string,
  dados: Partial<DadosDeAtividade> & { readonly concluida?: boolean },
): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/atividades/${id}`, { metodo: 'PATCH', corpo: dados });

export const excluirAtividade = (id: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/atividades/${id}`, { metodo: 'DELETE' });

// ─── Relatórios ─────────────────────────────────────────────────────────────

export interface EtapaDoFunil {
  readonly colunaId: string;
  readonly nome: string;
  readonly cards: number;
  readonly alcancaram: number;
  readonly tempoMedioDias: number | null;
  readonly passagens: number;
}

export interface CardParado {
  readonly cardId: string;
  readonly razaoSocial: string;
  readonly nomeFantasia: string | null;
  readonly colunaNome: string;
  readonly dias: number;
}

export interface RelatorioDoFunil {
  /** O quadro do funil — cada quadro tem as suas etapas. */
  readonly quadro: { readonly id: string; readonly nome: string };
  readonly totalDeCards: number;
  readonly etapas: readonly EtapaDoFunil[];
  readonly parados: readonly CardParado[];
  readonly atividadesAtrasadas: number;
  readonly historicoDesde: string | null;
}

export const carregarRelatorioDoFunil = (
  diasParado: number,
  quadroId: string | null,
): Promise<RelatorioDoFunil> =>
  requisicaoAutenticada<RelatorioDoFunil>(
    `/crm/relatorios/funil?${new URLSearchParams({
      hoje: hojeIso(),
      diasParado: String(diasParado),
      ...(quadroId === null ? {} : { quadroId }),
    }).toString()}`,
  );

// ─── Captadores ─────────────────────────────────────────────────────────────

export interface CaptadorDoCrm {
  readonly id: string;
  readonly tagId: string;
  readonly nome: string;
  readonly documento: string | null;
  readonly contato: string | null;
  /** Participação em % (0 a 100). */
  readonly percentual: number | null;
  readonly dadosBancarios: string | null;
  readonly observacoes: string | null;
}

export type DadosDoCaptador = Omit<CaptadorDoCrm, 'id' | 'tagId'>;

export const captadoresDoCard = (cardId: string): Promise<CaptadorDoCrm[]> =>
  requisicaoAutenticada<CaptadorDoCrm[]>(`/crm/cards/${cardId}/captadores`);

export const ativarCaptadores = (cardId: string, tagId: string, ligado: boolean): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/cards/${cardId}/tags/${tagId}/captadores`, {
    metodo: 'PUT',
    corpo: { comCaptadores: ligado },
  });

export const criarCaptador = (
  cardId: string,
  tagId: string,
  dados: DadosDoCaptador,
): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/cards/${cardId}/tags/${tagId}/captadores`, {
    metodo: 'POST',
    corpo: dados,
  });

export const editarCaptador = (id: string, dados: DadosDoCaptador): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/captadores/${id}`, { metodo: 'PATCH', corpo: dados });

export const excluirCaptador = (id: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/captadores/${id}`, { metodo: 'DELETE' });

// ─── Contatos (sem acesso ao Deca) ───────────────────────────────────────────

/**
 * Contato da empresa que não é colaborador: sem login, sem CPF, sem e-mail do
 * sistema. É da empresa, não do card (`crm-contatos.service.ts`).
 */
export interface ContatoDoCrm {
  readonly id: string;
  readonly nome: string | null;
  readonly cargo: string | null;
  readonly telefone: string | null;
  readonly email: string | null;
  readonly origem: 'PROSPECCAO' | 'MANUAL';
}

export interface DadosDoContato {
  readonly nome: string | null;
  readonly cargo: string | null;
  readonly telefone: string | null;
  readonly email: string | null;
}

export const criarContato = (cardId: string, dados: DadosDoContato): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/cards/${cardId}/contatos`, { metodo: 'POST', corpo: dados });

export const editarContato = (id: string, dados: DadosDoContato): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/contatos/${id}`, { metodo: 'PATCH', corpo: dados });

export const excluirContato = (id: string): Promise<void> =>
  requisicaoAutenticada<void>(`/crm/contatos/${id}`, { metodo: 'DELETE' });
